import React from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  FileText, 
  Video, 
  QrCode, 
} from "lucide-react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const Homepage = () => {
  const features = [
    {
      icon: <FileText className="h-12 w-12 text-blue-600" />,
      title: "Resume Builder",
      description: "Create professional, ATS-friendly resumes with our easy-to-use builder. Stand out with modern templates designed for Canadian employers."
    },
    {
      icon: <Video className="h-12 w-12 text-blue-600" />,
      title: "Video Interview",
      description: "Record professional video introductions that showcase your personality and communication skills beyond what a resume can convey."
    },
    {
      icon: <QrCode className="h-12 w-12 text-blue-600" />,
      title: "QR Code Integration",
      description: "Seamlessly connect your resume to your video profile with QR codes, giving employers instant access to your complete application."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <SiteHeader />

      {/* Hero Section — container widens subtly at xl/2xl so very
          wide desktop viewports don't feel airy at the edges. Inner
          sections (features, CTA) intentionally stay at the narrower
          7xl reading width. */}
      <section id="home" className="pt-16 pb-20 lg:pt-24 lg:pb-32">
        <div className="max-w-7xl xl:max-w-[84rem] 2xl:max-w-[92rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 xl:gap-20 items-center">
            <div className="mb-12 lg:mb-0">
              <Badge className="mb-6 bg-blue-100 text-blue-800 hover:bg-blue-200">
                Made for the Canadian Job Market
              </Badge>
              {/* Iter-32 headline polish: H1 is centered within its
                  own block so the three lines share a common
                  centerline and "Stand Out" reads as the deliberate
                  visual anchor of the stack. The subhead + CTAs stay
                  left-aligned so the overall hero column still has a
                  clear leading edge. */}
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight text-center">
                <span className="block">Helping Canadians</span>
                <span className="block text-blue-600 tracking-tight">Stand Out</span>
                <span className="block">in the Job Market</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed text-balance">
                Create professional resumes, compelling cover letters, and engaging video interviews 
                with QR code integration. Give employers a complete picture of who you are and 
                make your application unforgettable.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-3 shadow-lg hover:shadow-xl transition-all">
                    Get Started Free
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-blue-200 text-blue-600 hover:bg-blue-50 text-lg px-8 py-3" data-testid="hero-see-how-it-works-btn">
                    See How It Works
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Visual — "Inter-View cover letter standing out
                from the stack". Iter-31: capped width + auto-margin
                so the image has breathing room and doesn't dominate
                the hero. Mobile keeps a 88vw cap so the photo still
                reads big enough for the "standing out" concept to be
                obvious. */}
            <div className="relative mx-auto w-full max-w-md sm:max-w-lg lg:max-w-none lg:ml-auto lg:px-4">
              <picture>
                <source
                  type="image/webp"
                  srcSet="/branding/hero-stand-out.webp"
                />
                <img
                  src="/branding/hero-stand-out.jpg"
                  alt="An Inter-View cover letter rising above a stack of ordinary resumes on a wooden desk"
                  width={1536}
                  height={1024}
                  loading="eager"
                  decoding="async"
                  className="w-full h-auto rounded-2xl shadow-xl ring-1 ring-slate-200/60"
                  data-testid="hero-image"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Land Your Dream Job
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our integrated platform combines traditional resumes with modern video profiles, 
              creating a comprehensive application that gets noticed.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-blue-100 hover:border-blue-200">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 p-4 bg-blue-50 rounded-2xl group-hover:bg-blue-100 transition-colors">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 mb-2">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to make a stronger first impression?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Build your complete application package — resume, cover letter, and a short video
            introduction — in about ten minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="w-full sm:w-auto bg-white text-blue-600 hover:bg-gray-50 text-lg px-8 py-3 shadow-lg hover:shadow-xl transition-all">
                Start Your Journey
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8 py-3" data-testid="cta-learn-more-btn">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Homepage;